import { Cell, Grid, HFlow, Icon, InfoLabel, Tag, Text, Theme, Tooltip, useStyles, VFlow } from 'bold-ui'
import { green } from 'bold-ui/lib/styles/colors'
import { AccordionButton } from 'components/accordion/AccordionButton'
import { DateTime } from 'components/date'
import { AccordionField } from 'components/form/field/AccordionField'
import { css } from 'emotion'
import { CondicaoMaternalDbEnum, OrigemDadoEnum, SexoEnum } from 'graphql/types.generated'
import { noop } from 'lodash'
import React, { CSSProperties, Fragment } from 'react'
import { AccordionItem, AccordionItemPanel } from 'react-accessible-accordion'

import { CondicoesFormModel, origemDadosVacinacao } from '../model'
import { CondicoesVacinacaoDose } from './CondicoesVacinacaoDose'
import { RegistroDoseWrapperProps } from './RegistroDoseWrapper'
import { VacinacaoVisualizacaoButtons } from './VacinacaoVisualizacaoButtons'

export type RegistroDoseAccordionItemProps = RegistroDoseWrapperProps

export const RegistroDoseAccordionItem = (props: RegistroDoseWrapperProps) => {
  const {
    dataAplicacao,
    origemDados,
    registradoAtendimento,
    idUnicoRegistro,
    handleOnDelete = noop,
    handleOnEdit = noop,
    readOnly = false,
    estrategia,
    grupoAtendimento,
    lote,
    nomeFabricante,
    dataValidadeLote,
    viaDeAdministracao,
    observacoes,
    localDaAplicacao,
    condicaoMaternal,
    isViajante,
    isComunicanteHanseniase,
    imunobiologicoId,
    doseNome,
    doseSigla,
    nomeProfissional,
    cbo,
    unidadeSaude,
    idUnicoRegistroAprazamentoProxDose,
    dataRegistro,
    condicoesVacinacao,
    isAplicacaoEquivalente,
    hasOnlyEquivalentesAplicadas,
    cid10MotivoIndicacao,
    cid10MotivoIndicacaoCodigoCid,
    cboPrescritor,
    isPesquisaClinica,
    anvisaProtocoloEstudo,
    anvisaProtocoloVersao,
    anvisaNumeroRegistro,
    sexoIdentidadeGenero,
  } = props

  const { classes } = useStyles(createStyles)

  const renderDoseContent = () => {
    let registroDoseContentProps: RegistroDoseContentProps = {
      cbo: cbo,
      condicoesVacinacao: condicoesVacinacao ?? {
        comunicanteHanseniase: isComunicanteHanseniase,
        condicao: condicaoMaternal,
        viajante: isViajante,
      },
      lote: lote,
      nomeFabricante,
      dataRegistro,
      dataValidadeLote,
      estrategia,
      doseNome,
      doseSigla,
      imunobiologicoId,
      isComunicanteHanseniase,
      condicaoMaternal,
      isViajante,
      localDaAplicacao,
      nomeProfissional,
      observacoes,
      origemDados,
      registradoAtendimento,
      unidadeSaude,
      viaDeAdministracao,
      grupoAtendimento,
      isAplicacaoEquivalente,
      cid10MotivoIndicacao,
      cid10MotivoIndicacaoCodigoCid,
      cboPrescritor,
      isPesquisaClinica,
      anvisaProtocoloEstudo,
      anvisaProtocoloVersao,
      anvisaNumeroRegistro,
      sexoIdentidadeGenero,
    }

    return <RegistroDoseContent {...registroDoseContentProps} />
  }

  if (readOnly) {
    return renderDoseContent()
  }

  return (
    <VFlow vSpacing={0}>
      <AccordionField name='Registro' allowZeroExpanded>
        <AccordionItem>
          <AccordionButton style={classes.accordionButton}>
            <HFlow
              alignItems='center'
              justifyContent='space-between'
              style={css`
                padding: 0.5rem;
                min-width: 35rem;
                width: 100%;
              `}
            >
              <HFlow>
                <Icon icon='angleDown' size={1.5} />
                <HFlow hSpacing={0.3} alignItems='center'>
                  <Icon color={green.c40} icon='needleFilled' size={1.1} />
                  <Text fontWeight='bold'>Dose aplicada em </Text>
                  <DateTime
                    value={dataAplicacao}
                    style={css`
                      font-weight: bold;
                    `}
                    format='DD/MM/YYYY'
                  />
                </HFlow>
              </HFlow>
              <HFlow hSpacing={0.3} alignItems='center'>
                {hasOnlyEquivalentesAplicadas && isAplicacaoEquivalente && (
                  <Tooltip text='Essa é uma aplicação de uma dose equivalente'>
                    <Tag type='normal'>Equivalente</Tag>
                  </Tooltip>
                )}
                <Tooltip text={origemDadosVacinacao[origemDados]?.hint}>
                  <Tag type='info'>{origemDadosVacinacao[origemDados]?.label}</Tag>
                </Tooltip>
                {registradoAtendimento && (
                  <VacinacaoVisualizacaoButtons
                    idUnicoRegistroAprazamentoProxDose={idUnicoRegistroAprazamentoProxDose}
                    idUnicoRegistro={idUnicoRegistro}
                    tipoModal='APLICACAO'
                    handleOnDelete={handleOnDelete}
                    handleOnEdit={handleOnEdit}
                  />
                )}
              </HFlow>
            </HFlow>
          </AccordionButton>
          <AccordionItemPanel className={classes.panel}>{renderDoseContent()}</AccordionItemPanel>
        </AccordionItem>
      </AccordionField>
    </VFlow>
  )
}

interface RegistroDoseContentProps {
  estrategia: string
  doseSigla?: string
  doseNome?: string
  grupoAtendimento?: string
  lote: string
  nomeFabricante: string
  dataValidadeLote: string
  viaDeAdministracao: string
  localDaAplicacao: string
  observacoes: string
  registradoAtendimento: boolean
  condicaoMaternal: CondicaoMaternalDbEnum
  isViajante: boolean
  isComunicanteHanseniase: boolean
  imunobiologicoId: string
  nomeProfissional: string
  cbo: string
  unidadeSaude: string
  dataRegistro: string
  condicoesVacinacao: CondicoesFormModel
  origemDados: OrigemDadoEnum
  isAplicacaoEquivalente: boolean
  cid10MotivoIndicacao: string
  cid10MotivoIndicacaoCodigoCid: string
  cboPrescritor: string
  isPesquisaClinica: boolean
  anvisaProtocoloEstudo: string
  anvisaProtocoloVersao: string
  anvisaNumeroRegistro: string
  sexoIdentidadeGenero: SexoEnum
}

interface PesquisaClinicaContentProps {
  isPesquisaClinica: boolean
  anvisaProtocoloEstudo: string
  anvisaProtocoloVersao: string
  anvisaNumeroRegistro: string
}

const PesquisaClinicaContent = (props: PesquisaClinicaContentProps) => {
  const { isPesquisaClinica, anvisaProtocoloEstudo, anvisaProtocoloVersao, anvisaNumeroRegistro } = props

  return (
    <Cell size={12}>
      <InfoLabel placeholder='-' title='Pesquisa'>
        {isPesquisaClinica ? 'Sim' : 'Não'}
      </InfoLabel>

      {isPesquisaClinica && (
        <Fragment>
          <InfoLabel placeholder='-' title='Anvisa protocolo estudo'>
            {anvisaProtocoloEstudo?.capitalize()}
          </InfoLabel>
          <InfoLabel placeholder='-' title='Anvisa protocolo versão'>
            {anvisaProtocoloVersao?.capitalize()}
          </InfoLabel>
          <InfoLabel placeholder='-' title='Anvisa número registro'>
            {anvisaNumeroRegistro?.capitalize()}
          </InfoLabel>
        </Fragment>
      )}
    </Cell>
  )
}

const RegistroDoseContent = (props: RegistroDoseContentProps) => {
  const {
    estrategia,
    grupoAtendimento,
    lote,
    nomeFabricante,
    dataValidadeLote,
    viaDeAdministracao,
    localDaAplicacao,
    observacoes,
    registradoAtendimento,
    condicaoMaternal,
    isViajante,
    isComunicanteHanseniase,
    imunobiologicoId,
    doseNome,
    doseSigla,
    nomeProfissional,
    cbo,
    unidadeSaude,
    dataRegistro,
    condicoesVacinacao,
    origemDados,
    isAplicacaoEquivalente,
    cid10MotivoIndicacao,
    cid10MotivoIndicacaoCodigoCid,
    cboPrescritor,
    isPesquisaClinica,
    anvisaProtocoloVersao,
    anvisaProtocoloEstudo,
    anvisaNumeroRegistro,
    sexoIdentidadeGenero,
  } = props

  const shouldExpandCell = grupoAtendimento && isAplicacaoEquivalente

  return (
    <Grid
      gap={0}
      gapVertical={1}
      style={css`
        padding: 1rem;
        width: 36rem;
        word-wrap: break-word;
      `}
    >
      <Cell size={8}>
        <InfoLabel title='Estratégia'>{estrategia?.capitalize()}</InfoLabel>
      </Cell>
      {isAplicacaoEquivalente && (
        <Cell size={4}>
          <InfoLabel title='Dose aplicada'>{`${doseSigla || ' - '} - ${doseNome?.capitalize() || ' - '}`}</InfoLabel>
        </Cell>
      )}
      {grupoAtendimento && (
        <Cell size={shouldExpandCell ? 8 : 4}>
          <InfoLabel title='Grupo de atendimento'> {grupoAtendimento?.capitalize()} </InfoLabel>
        </Cell>
      )}
      <Cell size={shouldExpandCell ? 4 : 8}>
        <InfoLabel title='Lote/Fabricante'>
          {`${lote?.capitalize() || ' - '}/${nomeFabricante?.capitalize() || ' - '}`}
        </InfoLabel>
      </Cell>
      <Cell size={shouldExpandCell ? 8 : 4}>
        <InfoLabel title='Data de validade'>
          <DateTime value={dataValidadeLote} format='DD/MM/YYYY' />
        </InfoLabel>
      </Cell>
      <Cell size={shouldExpandCell ? 4 : 8}>
        <InfoLabel placeholder='-' title='Via de administração'>
          {viaDeAdministracao?.capitalize()}
        </InfoLabel>
      </Cell>
      <Cell size={shouldExpandCell ? 8 : 4}>
        <InfoLabel placeholder='-' title='Local da aplicação'>
          {localDaAplicacao?.capitalize()}
        </InfoLabel>
      </Cell>
      <Cell size={shouldExpandCell ? 4 : 8}>
        <InfoLabel placeholder='-' title='Motivo de indicação'>
          {cid10MotivoIndicacao?.capitalize()} - {cid10MotivoIndicacaoCodigoCid}
        </InfoLabel>
      </Cell>
      <Cell size={shouldExpandCell ? 8 : 4}>
        <InfoLabel placeholder='-' title='Especialidade do profissional prescritor'>
          {cboPrescritor?.capitalize()}
        </InfoLabel>
      </Cell>
      <PesquisaClinicaContent
        isPesquisaClinica={isPesquisaClinica}
        anvisaProtocoloVersao={anvisaProtocoloVersao}
        anvisaProtocoloEstudo={anvisaProtocoloEstudo}
        anvisaNumeroRegistro={anvisaNumeroRegistro}
      />
      <Cell size={12}>
        <InfoLabel title='Observações' placeholder='-'>
          {observacoes}
        </InfoLabel>
      </Cell>
      <CondicoesVacinacaoDose
        sexoIdentidadeGenero={sexoIdentidadeGenero}
        registradoAtendimento={registradoAtendimento}
        imunobiologicoId={imunobiologicoId}
        condicoesVacinacao={condicoesVacinacao}
        condicaoMaternal={condicaoMaternal}
        isViajante={isViajante}
        isComunicanteHanseniase={isComunicanteHanseniase}
      />
      <Cell size={12}>
        <InfoLabel title='Aplicado por'>{`${nomeProfissional} (${cbo})`}</InfoLabel>
        {unidadeSaude}
      </Cell>
      <Cell>
        <Text fontWeight='bold'>Aplicação registrada em </Text>
        <DateTime
          value={dataRegistro}
          style={css`
            font-weight: bold;
          `}
          format={origemDados === OrigemDadoEnum.PEC ? 'DD/MM/YYYY [às] HH:mm' : 'DD/MM/YYYY'}
        />
      </Cell>
    </Grid>
  )
}

const createStyles = (theme: Theme) => ({
  panel: {
    margin: '0 0.3rem',
    borderWidth: '0 0.063rem 0.063rem',
    borderStyle: 'solid',
    borderColor: theme.pallete.gray.c60,
    position: 'relative',
    top: '-0.6rem',
  } as CSSProperties,
  accordionButton: {
    display: 'flex',
    padding: '0.5rem',
    border: `0.063rem solid ${theme.pallete.gray.c60}`,
    borderBlockEnd: `0.063rem solid ${theme.pallete.gray.c80}`,
  } as CSSProperties,
})
